'use client'

import React, { useEffect, useState } from 'react'
import { OffersOffer, PaginationType } from '@/services/dataTypes'
import { filterOfferBanners, getBannerDimensions } from '@/constants/hooks'
import { apiOfferBanners } from '@/apis/offers'
import Banner from './Banner'
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react'

interface Props {
    bannerResponse: OffersOffer[]
    domain: string
    mer_slug: string
    slug_type: string
    merchantId: string
    companyId: string
    pagination: PaginationType
}

const VerticalOfferBanner = ({ bannerResponse, domain, mer_slug, slug_type, merchantId, companyId, pagination }: Props) => {
    const [banners, setBanners] = useState<OffersOffer[]>([])
    const [page, setPage] = useState(2)
    const [hasMore, setHasMore] = useState(true)
    const [loading, setLoading] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    useEffect(() => {
        setBanners(filterOfferBanners(bannerResponse, 20, 2000, 20, 2000))
        if (!pagination?.next_page) setHasMore(false)
    }, [bannerResponse])

    const loadMore = async () => {
        if (loading || !hasMore) return
        setLoading(true)
        try {
            const res = await apiOfferBanners(merchantId, companyId, page)
            const newBanners = filterOfferBanners(res.data?.offers || [], 20, 2000, 20, 2000)
            setBanners(prev => [...prev, ...newBanners])
            if (res?.data?.pagination?.next_page) { setPage(p => p + 1); setIsExpanded(true) }
            else setHasMore(false)
        } catch (e) { console.error(e) }
        finally { setLoading(false) }
    }

    const showLess = () => {
        setBanners(filterOfferBanners(bannerResponse, 20, 2000, 20, 2000))
        setPage(2); setHasMore(true); setIsExpanded(false)
    }

    if (!banners.length) return null

    return (
        <div className="bg-white rounded-3xl border border-slate-100 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-4">Banner Offers</h4>
            <div className="space-y-3">
                {banners.map((offer_data, i) => {
                    const dimension = getBannerDimensions(offer_data)
                    return (
                        <Banner key={i} data={offer_data} height={dimension?.height} width={dimension?.width}
                            mer_slug={mer_slug} slug_type={slug_type} domain={domain} />
                    )
                })}
            </div>
            <div className={`flex gap-2 mt-4 ${isExpanded ? 'justify-between' : 'justify-center'}`}>
                {hasMore && (
                    <button onClick={loadMore} disabled={loading}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold text-[#00a87a] border border-slate-200 hover:border-[#00d69f] rounded-xl hover:bg-emerald-50/40 transition-colors disabled:opacity-50 cursor-pointer">
                        {loading ? <Loader2 size={12} className="animate-spin" /> : <ChevronDown size={12} />}
                        <span>{loading ? 'Loading...' : 'Show More Banners'}</span>
                    </button>
                )}
                {isExpanded && (
                    <button onClick={showLess}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-bold text-slate-500 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer">
                        <ChevronUp size={12} /> <span>Show Less</span>
                    </button>
                )}
            </div>
        </div>
    )
}

export default VerticalOfferBanner
